import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import {
  paymentApi,
  Transaction,
  TransactionBillEditContext,
  UpdateTransactionBillRequest,
} from '../../api/paymentApi';
import { formatVndInput, parseVndInput } from '../../utils/vndFormat';
import { getApiErrorMessage } from '../../utils/apiErrors';
import { parseContractAmountField } from '../../utils/contractAmount.util';
import { serviceFeeApi } from '../../api/serviceFeeApi';
import {
  buildCasualParkingVariancePayloadLines,
  CasualParkingVarianceLinesEditor,
  emptyCasualParkingVarianceLine,
  type CasualParkingVarianceLineForm,
  validateCasualParkingVariancePartnerSelection,
} from '../service-fees/CasualParkingVarianceLinesEditor';

interface TransactionBillEditModalProps {
  isOpen: boolean;
  transaction: Transaction | null;
  onClose: () => void;
  onSaved: () => void;
}

type VarianceType = 'DEDUCTION' | 'VOUCHER';

const toDateInputValue = (iso: string) => {
  const d = new Date(iso);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const formatVnd = (n: number) => `${Math.round(n).toLocaleString('vi-VN')} đ`;

export const TransactionBillEditModal: React.FC<TransactionBillEditModalProps> = ({
  isOpen,
  transaction,
  onClose,
  onSaved,
}) => {
  const [paymentMethod, setPaymentMethod] = useState<'CASH' | 'BANK_TRANSFER'>('CASH');
  const [paymentDate, setPaymentDate] = useState('');
  const [amountReceivedInput, setAmountReceivedInput] = useState('');
  const [machineAmount, setMachineAmount] = useState('');
  const [varianceLines, setVarianceLines] = useState<CasualParkingVarianceLineForm[]>([]);
  const [casualBuildingId, setCasualBuildingId] = useState('');
  const [casualBillingPeriod, setCasualBillingPeriod] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [loadingContext, setLoadingContext] = useState(false);
  const [cashVarianceSettledOnOtherReceipt, setCashVarianceSettledOnOtherReceipt] = useState(false);
  const [projectBankAccountId, setProjectBankAccountId] = useState('');
  const [projectBankAccounts, setProjectBankAccounts] = useState<
    TransactionBillEditContext['projectBankAccounts']
  >([]);
  const [initialProjectBankAccountId, setInitialProjectBankAccountId] = useState<string | null>(null);
  const [canAssignCasualParkingFeeType, setCanAssignCasualParkingFeeType] = useState(false);
  const [needsCasualParkingReceiptSync, setNeedsCasualParkingReceiptSync] = useState(false);
  const [assignCasualParkingFeeType, setAssignCasualParkingFeeType] = useState(false);
  const [createdByLabel, setCreatedByLabel] = useState('');
  const [initialCasualSnapshot, setInitialCasualSnapshot] = useState<{
    machineAmount: number;
    lines: Array<{
      varianceType: VarianceType;
      counterpartyName: string;
      amount: number;
    }>;
  } | null>(null);

  const appliedAmount = transaction?.appliedAmount ?? transaction?.amount ?? 0;
  const creditApplied = transaction?.creditApplied ?? 0;
  const currentReceived = transaction?.amountReceived ?? transaction?.amount ?? 0;
  const currentOverpayment = transaction?.overpaymentAmount ?? 0;
  const isCasualParking = Boolean(transaction?.isCasualParkingSingleReceipt);

  const parsedReceived = useMemo(() => parseVndInput(amountReceivedInput), [amountReceivedInput]);
  const machineNum = parseContractAmountField(machineAmount);
  const actualNum =
    parsedReceived !== undefined ? Math.round(parsedReceived) : Math.round(currentReceived);
  const varianceTotal = varianceLines.reduce(
    (sum, line) => sum + (parseContractAmountField(line.amount) || 0),
    0,
  );
  const expectedVariance = Math.max(0, machineNum - actualNum);
  const varianceRemaining = expectedVariance - varianceTotal;

  const showVarianceSection = useMemo(
    () => isCasualParking && machineNum > 0 && machineNum > actualNum,
    [isCasualParking, machineNum, actualNum],
  );

  useEffect(() => {
    if (!isOpen || !transaction) return;
    const method = transaction.paymentMethod;
    setPaymentMethod(method === 'BANK_TRANSFER' ? 'BANK_TRANSFER' : 'CASH');
    setPaymentDate(toDateInputValue(transaction.transactionDate));
    setAmountReceivedInput(formatVndInput(currentReceived));
    setNotes('');
    setMachineAmount('');
    setVarianceLines([]);
    setCasualBuildingId('');
    setCasualBillingPeriod('');
    setInitialCasualSnapshot(null);
    setCashVarianceSettledOnOtherReceipt(false);
    setProjectBankAccountId('');
    setProjectBankAccounts([]);
    setInitialProjectBankAccountId(null);
    setCanAssignCasualParkingFeeType(false);
    setNeedsCasualParkingReceiptSync(false);
    setAssignCasualParkingFeeType(false);
    setCreatedByLabel('');

    const billId = transaction.billId || transaction.id;
    paymentApi
      .getTransactionBillEditContext(billId)
      .then((ctx) => {
        setProjectBankAccounts(ctx.projectBankAccounts);
        setInitialProjectBankAccountId(ctx.projectBankAccountId);
        setCanAssignCasualParkingFeeType(Boolean(ctx.canAssignCasualParkingFeeType));
        setNeedsCasualParkingReceiptSync(Boolean(ctx.needsCasualParkingReceiptSync));
        setAssignCasualParkingFeeType(Boolean(ctx.needsCasualParkingReceiptSync));
        setCreatedByLabel(ctx.createdByName ?? '');
        const defaultId =
          ctx.projectBankAccountId ||
          ctx.projectBankAccounts.find((a) => a.isDefault)?.id ||
          ctx.projectBankAccounts[0]?.id ||
          '';
        setProjectBankAccountId(defaultId);
      })
      .catch(() => {
        toast.error('Không tải được tài khoản dự án');
      });

    if (!transaction.isCasualParkingSingleReceipt) return;

    setLoadingContext(true);
    paymentApi
      .getCasualParkingBillEditContext(billId)
      .then((ctx) => {
        setCasualBuildingId(ctx.buildingId);
        setCasualBillingPeriod(ctx.billingPeriod);
        setMachineAmount(
          ctx.machineAmount != null && ctx.machineAmount > 0
            ? formatVndInput(String(ctx.machineAmount))
            : '',
        );
        const mappedLines = ctx.varianceLines.map((line) => ({
          varianceType: line.varianceType,
          counterpartyName: line.counterpartyName,
          amount: formatVndInput(String(line.amount)),
          notes: line.notes ?? '',
          contractApartmentId: '',
          contractPartnerKey: '',
        }));
        setVarianceLines(mappedLines);
        setInitialCasualSnapshot({
          machineAmount: Math.round(ctx.machineAmount ?? 0),
          lines: ctx.varianceLines.map((line) => ({
            varianceType: line.varianceType,
            counterpartyName: line.counterpartyName.trim(),
            amount: Math.round(line.amount),
          })),
        });
      })
      .catch(() => {
        toast.error('Không tải được thông tin xe vãng lai');
      })
      .finally(() => setLoadingContext(false));
  }, [isOpen, transaction, currentReceived]);

  useEffect(() => {
    if (!showVarianceSection) {
      setVarianceLines([]);
      return;
    }
    if (cashVarianceSettledOnOtherReceipt) {
      return;
    }
    if (varianceLines.length === 0) {
      setVarianceLines([emptyCasualParkingVarianceLine()]);
    }
  }, [showVarianceSection, cashVarianceSettledOnOtherReceipt]);

  if (!isOpen || !transaction) return null;

  const billId = transaction.billId || transaction.id;

  const normalizeVarianceKey = (
    lines: Array<{ varianceType: VarianceType; counterpartyName: string; amount: number }>,
  ) =>
    [...lines]
      .sort((a, b) =>
        `${a.varianceType}:${a.counterpartyName}:${a.amount}`.localeCompare(
          `${b.varianceType}:${b.counterpartyName}:${b.amount}`,
        ),
      )
      .map((line) => `${line.varianceType}:${line.counterpartyName}:${line.amount}`)
      .join('|');

  const casualParkingDetailsChanged = () => {
    if (!isCasualParking || !initialCasualSnapshot) return false;
    const parsedMachine = parseContractAmountField(machineAmount);
    if (parsedMachine !== initialCasualSnapshot.machineAmount) return true;
    const current = buildCasualParkingVariancePayloadLines(varianceLines).map((line) => ({
      varianceType: line.varianceType,
      counterpartyName: line.counterpartyName,
      amount: line.amount,
    }));
    return normalizeVarianceKey(current) !== normalizeVarianceKey(initialCasualSnapshot.lines);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const payload: UpdateTransactionBillRequest = {};
    if (paymentMethod !== transaction.paymentMethod) {
      payload.paymentMethod = paymentMethod;
    }
    const originalDate = toDateInputValue(transaction.transactionDate);
    if (paymentDate && paymentDate !== originalDate) {
      payload.paymentDate = paymentDate;
    }
    const amountChanged =
      parsedReceived !== undefined && Math.round(parsedReceived) !== Math.round(currentReceived);
    if (amountChanged) {
      payload.amountReceived = Math.round(parsedReceived!);
    }
    if (notes.trim()) {
      payload.notes = notes.trim();
    }
    const casualDetailsChanged = casualParkingDetailsChanged();
    const projectBankChanged =
      Boolean(projectBankAccountId) &&
      projectBankAccountId !== (initialProjectBankAccountId ?? '');

    if (
      !payload.paymentMethod &&
      !payload.paymentDate &&
      payload.amountReceived === undefined &&
      !casualDetailsChanged &&
      !projectBankChanged &&
      !assignCasualParkingFeeType
    ) {
      toast.error('Chưa có thay đổi');
      return;
    }
    if (
      (amountChanged ||
        casualDetailsChanged ||
        projectBankChanged ||
        assignCasualParkingFeeType) &&
      !notes.trim()
    ) {
      toast.error('Vui lòng ghi chú lý do khi chỉnh sửa giao dịch');
      return;
    }

    const onlyCashAmountCorrection =
      isCasualParking &&
      amountChanged &&
      !casualDetailsChanged &&
      cashVarianceSettledOnOtherReceipt &&
      parsedReceived !== undefined &&
      Math.round(parsedReceived) < Math.round(currentReceived);

    if (isCasualParking && (amountChanged || casualDetailsChanged)) {
      const parsedMachine = parseContractAmountField(machineAmount);
      const parsedActual = amountChanged
        ? Math.round(parsedReceived!)
        : Math.round(currentReceived);
      if (!onlyCashAmountCorrection) {
        if (parsedMachine > 0 && parsedMachine !== parsedActual + varianceTotal) {
          toast.error(
            `Chênh lệch còn ${formatVndInput(String(Math.max(0, parsedMachine - parsedActual - varianceTotal)))}đ — hãy phân bổ Khấu trừ / Voucher`,
          );
          return;
        }
      }
      if (!onlyCashAmountCorrection) {
        payload.machineAmount = parsedMachine;
      }
      const payloadLines = buildCasualParkingVariancePayloadLines(varianceLines);
      if (
        showVarianceSection &&
        !cashVarianceSettledOnOtherReceipt &&
        payloadLines.length === 0
      ) {
        toast.error('Vui lòng thêm ít nhất một dòng Khấu trừ hoặc Voucher');
        return;
      }
      if (!onlyCashAmountCorrection) {
        payload.varianceLines = payloadLines;
      } else {
        payload.cashVarianceSettledOnOtherReceipt = true;
      }
      if (casualBuildingId && payloadLines.length > 0 && !onlyCashAmountCorrection) {
        const partnersRes = await serviceFeeApi.getCasualParkingVoucherContractTargets(
          casualBuildingId,
          '',
        );
        const voucherPartnerOptions = (partnersRes.partners ?? []).map((p) => ({
          optionKey: p.optionKey,
          contractCode: p.targets[0]?.contractCode ?? '',
          counterpartyName: p.counterpartyName,
          displayLabel: p.displayLabel,
          targets: p.targets.map((t) => ({
            contractApartmentId: t.contractApartmentId,
            contractCode: t.contractCode,
            apartmentCode: t.apartmentCode,
            customerName: t.customerName,
          })),
        }));
        const partnerError = validateCasualParkingVariancePartnerSelection(
          varianceLines,
          voucherPartnerOptions,
        );
        if (partnerError) {
          toast.error(partnerError);
          return;
        }
      }
    }

    if (projectBankChanged) {
      payload.projectBankAccountId = projectBankAccountId;
    }
    if (assignCasualParkingFeeType) {
      payload.assignCasualParkingFeeType = true;
    }

    setSaving(true);
    try {
      await paymentApi.updateTransactionBill(billId, payload);
      toast.success('Đã cập nhật giao dịch');
      onSaved();
      onClose();
    } catch (error: unknown) {
      toast.error(getApiErrorMessage(error, 'Không thể cập nhật giao dịch'));
    } finally {
      setSaving(false);
    }
  };

  const previewOverpayment = isCasualParking
    ? 0
    : parsedReceived !== undefined
      ? Math.max(0, Math.round(parsedReceived) + creditApplied - Math.round(appliedAmount))
      : currentOverpayment;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="border-b px-5 py-4 flex justify-between items-center sticky top-0 bg-white">
          <h3 className="text-lg font-semibold text-gray-900">Chỉnh sửa giao dịch</h3>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600">✕</button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div className="text-sm text-gray-600 space-y-1">
            <div>
              Phiếu thu: <strong>{transaction.receiptNumber || '—'}</strong>
            </div>
            <div>
              Căn hộ: <strong>{transaction.apartmentCode}</strong>
            </div>
            {createdByLabel ? (
              <div>
                Người lập phiếu: <strong>{createdByLabel}</strong>
              </div>
            ) : null}
          </div>

          {canAssignCasualParkingFeeType ? (
            <div className="rounded-lg border border-amber-200 bg-amber-50/80 px-3 py-3 space-y-2">
              <label className="flex items-start gap-2 text-sm text-gray-900 cursor-pointer">
                <input
                  type="checkbox"
                  className="mt-0.5"
                  checked={assignCasualParkingFeeType}
                  onChange={(e) => setAssignCasualParkingFeeType(e.target.checked)}
                />
                <span>
                  {needsCasualParkingReceiptSync ? (
                    <>
                      <strong>Đồng bộ lại phiếu thu xe vãng lai (T-VL001)</strong> — sửa phân bổ hóa
                      đơn và Diễn giải trên sổ (phiếu đã gán loại thu trước đó nhưng sổ chưa cập nhật).
                    </>
                  ) : (
                    <>
                      <strong>Gán loại thu: Thu tiền xe vãng lai (T-VL001)</strong> — tạo/ghép hóa đơn xe
                      vãng lai, cập nhật mẫu phiếu thu và loại trên sổ thu chi (dùng khi phiếu thu dư /
                      hóa đơn khác thay vì Thêm hóa đơn xe vãng lai).
                    </>
                  )}
                </span>
              </label>
            </div>
          ) : null}

          <div className="rounded-lg bg-gray-50 border border-gray-100 px-3 py-2 text-sm text-gray-700 space-y-1">
            {isCasualParking ? (
              <>
                <div className="text-amber-900 font-medium">Phiếu thu xe vãng lai</div>
                <div>
                  Số tiền trên hóa đơn (lúc tạo): <strong>{formatVnd(appliedAmount)}</strong>
                </div>
                <p className="text-xs text-gray-600">
                  Khi máy &gt; thực thu, phân bổ <strong>Khấu trừ / Voucher</strong> như khi thêm hóa
                  đơn. Nếu phần chênh lệch đã thu trên <strong>phiếu khác</strong> (vd. CK), tick ô bên
                  dưới, giảm thực thu và ghi chú — không phân bổ trên phiếu này.
                </p>
              </>
            ) : (
              <>
                <div>Tổng phân bổ hóa đơn: <strong>{formatVnd(appliedAmount)}</strong></div>
                {creditApplied > 0 && (
                  <div>Cấn trừ từ dư: <strong>{formatVnd(creditApplied)}</strong></div>
                )}
                <div>Thu dư hiện tại: <strong>{formatVnd(currentOverpayment)}</strong></div>
              </>
            )}
          </div>

          {isCasualParking && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Số tiền trên máy giữ xe</label>
              <input
                type="text"
                inputMode="numeric"
                disabled={loadingContext}
                value={machineAmount}
                onChange={(e) => setMachineAmount(formatVndInput(e.target.value))}
                className="w-full rounded-lg border px-3 py-2 text-sm"
                placeholder="0"
              />
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Số tiền thực thu</label>
            <input
              type="text"
              inputMode="numeric"
              value={amountReceivedInput}
              onChange={(e) => setAmountReceivedInput(formatVndInput(e.target.value))}
              className="w-full rounded-lg border px-3 py-2 text-sm"
              placeholder="0"
            />
            {parsedReceived !== undefined &&
              Math.round(parsedReceived) !== Math.round(currentReceived) &&
              !isCasualParking && (
                <p className="mt-1 text-xs text-gray-500">
                  Thu dư sau sửa (dự kiến): <strong>{formatVnd(previewOverpayment)}</strong>
                </p>
              )}
          </div>

          {showVarianceSection ? (
            <div className="space-y-3 rounded-lg border border-amber-100 bg-amber-50/50 px-3 py-3">
              <label className="flex items-start gap-2 text-sm text-gray-800 cursor-pointer">
                <input
                  type="checkbox"
                  className="mt-0.5"
                  checked={cashVarianceSettledOnOtherReceipt}
                  onChange={(e) => setCashVarianceSettledOnOtherReceipt(e.target.checked)}
                />
                <span>
                  Phần chênh lệch ({formatVnd(expectedVariance)}) đã thu trên phiếu khác — không phân
                  bổ Khấu trừ / Voucher trên phiếu này
                </span>
              </label>
              {!cashVarianceSettledOnOtherReceipt && casualBuildingId ? (
                <CasualParkingVarianceLinesEditor
                  buildingId={casualBuildingId}
                  billingPeriod={casualBillingPeriod}
                  varianceLines={varianceLines}
                  setVarianceLines={setVarianceLines}
                  expectedVariance={expectedVariance}
                  varianceRemaining={varianceRemaining}
                  active={isOpen && isCasualParking}
                />
              ) : null}
            </div>
          ) : null}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Tài khoản dự án</label>
            <select
              value={projectBankAccountId}
              onChange={(e) => setProjectBankAccountId(e.target.value)}
              className="w-full rounded-lg border px-3 py-2 text-sm"
              disabled={projectBankAccounts.length === 0}
            >
              {projectBankAccounts.length === 0 ? (
                <option value="">Chưa có tài khoản dự án</option>
              ) : (
                projectBankAccounts.map((account) => (
                  <option key={account.id} value={account.id}>
                    {[account.name, account.bankAccountNumber, account.bankName]
                      .filter(Boolean)
                      .join(' — ')}
                  </option>
                ))
              )}
            </select>
            <p className="mt-1 text-xs text-gray-500">
              Dùng khi phiếu thu trước đó ghi vào thu dư căn hộ (chưa có tài khoản dự án). Hệ thống
              chuyển số tiền sang tài khoản dự án và cập nhật mẫu phiếu thu (xe vãng lai / thu chi).
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Hình thức thanh toán</label>
            <select
              value={paymentMethod}
              onChange={(e) => setPaymentMethod(e.target.value as 'CASH' | 'BANK_TRANSFER')}
              className="w-full rounded-lg border px-3 py-2 text-sm"
            >
              <option value="CASH">Tiền mặt</option>
              <option value="BANK_TRANSFER">Chuyển khoản</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Ngày thanh toán</label>
            <input
              type="date"
              value={paymentDate}
              onChange={(e) => setPaymentDate(e.target.value)}
              className="w-full rounded-lg border px-3 py-2 text-sm"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Ghi chú (lý do chỉnh sửa)</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              className="w-full rounded-lg border px-3 py-2 text-sm"
              placeholder="Bắt buộc khi sửa thực thu hoặc khấu trừ/voucher"
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 border rounded-lg text-sm">
              Hủy
            </button>
            <button
              type="submit"
              disabled={saving || loadingContext}
              className="px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg text-sm disabled:opacity-50"
            >
              {saving ? 'Đang lưu...' : 'Lưu thay đổi'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TransactionBillEditModal;
